import { camelCase } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { RelationshipType } from '@sierralabs/entity-core';
import { useTrackChanges } from '@sierralabs/track-changes-react';
import { useFindEntityDeltas } from './useFindEntityDeltas';
import { useApplyEntityDelta } from './useApplyEntityDelta';
export function useEntityConfigurator(plugins) {
    const [selectedEntity, setSelectedEntity] = useState();
    const [isQueued, setIsQueued] = useState(false);
    const timeoutRef = useRef();
    const pluginsRef = useRef(plugins);
    const [validationError, setValidationError] = useState({
        columns: [],
        relations: []
    });
    const trackChanges = useTrackChanges(selectedEntity || {});
    const { entityDeltaList, getEntityDeltas } = useFindEntityDeltas((selectedEntity === null || selectedEntity === void 0 ? void 0 : selectedEntity.id) || '');
    // restart track changes if switching entities
    useEffect(() => {
        if (!selectedEntity)
            return;
        if (selectedEntity.id !== trackChanges.instance.id ||
            // compare name for locked/static entities
            (selectedEntity.editable &&
                selectedEntity.name !== trackChanges.instance.name)) {
            trackChanges.restart(selectedEntity);
            setValidationError({ columns: [], relations: [] });
            // get change history
            if (selectedEntity.id)
                getEntityDeltas();
        }
    }, [selectedEntity, trackChanges]);
    useEffect(() => {
        if (entityDeltaList) {
            trackChanges.history.undo.unshift(...entityDeltaList.data.map(item => item.delta));
        }
    }, [entityDeltaList, trackChanges.history.undo]);
    // handle applying deltas to server side
    const { updatedResult, isUpdating, updateError, applyEntityDeltas } = useApplyEntityDelta((selectedEntity === null || selectedEntity === void 0 ? void 0 : selectedEntity.id) || '');
    const { uncommit } = trackChanges;
    useEffect(() => {
        if (updatedResult || updateError) {
            setIsQueued(false);
        }
        if (updateError) {
            // undo commit if error
            uncommit();
        }
    }, [uncommit, updateError, updatedResult]);
    /**
     * Validate the Entity Schema
     */
    const validateSchema = useCallback((entitySchema) => {
        var _a, _b, _c;
        if (!entitySchema)
            entitySchema = trackChanges.final;
        const validationError = {
            name: {
                touched: true,
                message: ((_a = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.name) === null || _a === void 0 ? void 0 : _a.trim().length) === 0
                    ? 'Entity name is required'
                    : undefined
            },
            columns: [],
            relations: []
        };
        let valid = !((_b = validationError.name) === null || _b === void 0 ? void 0 : _b.message);
        entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns.forEach((column, index) => {
            var _a, _b, _c, _d;
            if (!validationError.columns[index])
                validationError.columns[index] = {};
            if (column.displayName.trim().length === 0) {
                validationError.columns[index] = {
                    displayName: { touched: true, message: 'Field name required.' }
                };
            }
            else if (
            // Check duplicate column name
            entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.columns.some(c => c !== column &&
                camelCase(c.displayName) === camelCase(column.displayName))) {
                validationError.columns[index] = {
                    displayName: { touched: true, message: 'Duplicate field name.' }
                };
            }
            // check plugin validation
            const plugin = pluginsRef.current.find(p => p.type === column.type);
            if (plugin === null || plugin === void 0 ? void 0 : plugin.validate) {
                const validationMessage = plugin.validate(column);
                validationError.columns[index].type = {
                    touched: true,
                    message: typeof validationMessage === 'boolean'
                        ? validationMessage
                            ? ''
                            : 'Failed validation'
                        : validationMessage || ''
                };
            }
            else {
                validationError.columns[index].type = {
                    touched: true,
                    message: ''
                };
            }
            if (valid)
                valid = Boolean(!((_b = (_a = validationError.columns[index]) === null || _a === void 0 ? void 0 : _a.displayName) === null || _b === void 0 ? void 0 : _b.message) &&
                    !((_d = (_c = validationError.columns[index]) === null || _c === void 0 ? void 0 : _c.type) === null || _d === void 0 ? void 0 : _d.message));
        });
        (_c = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.relations) === null || _c === void 0 ? void 0 : _c.forEach((relation, index) => {
            var _a, _b, _c, _d, _e, _f, _g;
            validationError.relations[index] = {
                displayName: {
                    touched: true,
                    message: !relation.displayName || relation.displayName.trim().length === 0
                        ? 'Field name required.'
                        : ((_a = entitySchema === null || entitySchema === void 0 ? void 0 : entitySchema.relations) === null || _a === void 0 ? void 0 : _a.some(r => r !== relation &&
                            camelCase(r.displayName) ===
                                camelCase(relation.displayName)))
                            ? 'Duplicate field name.'
                            : ''
                },
                target: {
                    touched: true,
                    message: !relation.target || relation.target.trim().length === 0
                        ? 'Target is required'
                        : ''
                },
                joinColumn: {
                    touched: true,
                    message: (relation.type === RelationshipType.ManyToOne ||
                        relation.type === RelationshipType.ManyToMany) &&
                        !relation.joinColumn
                        ? 'Join column is required'
                        : relation.type === RelationshipType.OneToMany &&
                            !relation.inverseSide
                            ? 'Specify an inverse target column'
                            : ''
                },
                joinTable: {
                    touched: true,
                    message: relation.type === RelationshipType.ManyToMany &&
                        typeof relation.joinTable !== 'object'
                        ? 'Join table is required'
                        : undefined
                },
                inverseJoinColumn: {
                    touched: true,
                    message: relation.type === RelationshipType.ManyToMany &&
                        typeof relation.joinTable === 'object' &&
                        !relation.joinTable
                            .inverseJoinColumns
                        ? 'Inverse join column is required'
                        : undefined
                }
            };
            if (valid)
                valid = Boolean(!((_c = (_b = validationError.relations[index]) === null || _b === void 0 ? void 0 : _b.displayName) === null || _c === void 0 ? void 0 : _c.message) &&
                    !((_e = (_d = validationError.relations[index]) === null || _d === void 0 ? void 0 : _d.target) === null || _e === void 0 ? void 0 : _e.message) &&
                    !((_g = (_f = validationError.relations[index]) === null || _f === void 0 ? void 0 : _f.joinColumn) === null || _g === void 0 ? void 0 : _g.message));
        });
        setValidationError(validationError);
        return Boolean(valid);
    }, [trackChanges.final, setValidationError]);
    // when trackChanges is used debounce and save to backend API
    useEffect(() => {
        if (!trackChanges.final.id)
            return;
        if (trackChanges.history.uncommitted.length > 0 && validateSchema()) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(() => {
                const deltas = trackChanges.commit();
                applyEntityDeltas(deltas);
            }, 500);
            setIsQueued(true);
        }
    }, [trackChanges.final, applyEntityDeltas, validateSchema]);
    return {
        isQueued,
        isUpdating,
        updatedResult,
        validationError,
        updateError,
        trackChanges,
        selectedEntity,
        setSelectedEntity,
        validateSchema
    };
}
//# sourceMappingURL=useEntityConfigurator.js.map